import { jwtDecode } from "jwt-decode";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { authRepository, Register, Signin, User } from "../api/authRepository";
import { pl } from "zod/v4/locales";

interface AuthContextType {
  token: string | null;
  email: string | null;
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  role: string | null;
  vendorId: string | null;
  orgId: string | null;
  DepartmentId: string | null;
  LocationId: string | null;
  //LeasedOrg: string | null;
  login: (login: Signin) => Promise<void>;
  signup: (signup: Register) => Promise<void>;
  forgotpassword: (email: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(() => localStorage.getItem("role"));
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("jwt"));
  const [email, setEmail] = useState<string | null>(() => localStorage.getItem("email"));
  const [vendorId, setVendorId] = useState<string | null>(() => localStorage.getItem("vendorId"));
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem("orgId"));
  const [DepartmentId, setDepartmentId] = useState<string | null>(() => localStorage.getItem("DepartmentId"));
  const [LocationId, setLocationId] = useState<string | null>(() => localStorage.getItem("LocationId"));
  //const [LeasedOrg, setLeasedOrg] = useState<string | null>(() => localStorage.getItem("LeasedOrg"));

  const login = async (login: Signin) => {
    const { token: newToken, refreshToken } = await authRepository.Login(login);

    localStorage.setItem("jwt", newToken);
    localStorage.setItem("refreshToken", refreshToken);
    setToken(newToken);
    const decoded: any = jwtDecode(newToken);

    const userId = decoded["id"];
    const roleFromToken = decoded["role"];
    const emailFromToken = decoded["email"];
    const vendorIdFromToken = decoded["vendorId"];
    const orgIdFromToken = decoded["orgId"];
    const DepartmentIdFromToken = decoded["departmentId"];
    const locationIdFromtoken = decoded["locationId"];

    if (roleFromToken) {
      setRole(roleFromToken);
      //localStorage.setItem("role", roleFromToken);
    }
    if (vendorIdFromToken) {
      setVendorId(vendorIdFromToken);
     // localStorage.setItem("vendorId", vendorIdFromToken);
    }
    if (orgIdFromToken) {
      setOrgId(orgIdFromToken);
      localStorage.setItem("orgId", orgIdFromToken);
    }
    if (DepartmentIdFromToken) {
      setDepartmentId(DepartmentIdFromToken);
     // localStorage.setItem("DepartmentId", DepartmentIdFromToken);
    }
    if (emailFromToken) {
      setEmail(emailFromToken);
      localStorage.setItem("email", emailFromToken);
    }
    if (locationIdFromtoken) {
      setLocationId(locationIdFromtoken);
      localStorage.setItem("LocationId", locationIdFromtoken);
    }
    if (userId) {
      const userData = await authRepository.getUserById(userId);
      setUser(userData);
    }
  };

  const signup = async (signup: Register) => {
    const { token: newToken, refreshToken } = await authRepository.register(signup);
    localStorage.setItem("jwt", newToken);
    localStorage.setItem("refreshToken", refreshToken);
    setToken(newToken);

    try {
      const decoded: any = jwtDecode(newToken);
      const userId = decoded["id"];
      const emailFromToken = decoded["email"] || decoded["sub"];
      const vendorIdFromToken =
        decoded["vendorId"] ||
        decoded["VendorId"] ||
        decoded?.vendor?.id ||
        decoded?.vendor?._id;
      const orgIdFromToken = decoded["orgId"] || decoded["OrgId"];
      const DepartmentIdFromToken = decoded["departmentId"];

      if (emailFromToken) {
        setEmail(emailFromToken);
        localStorage.setItem("email", emailFromToken);
      }

      if (vendorIdFromToken) {
        setVendorId(vendorIdFromToken);
        localStorage.setItem("vendorId", vendorIdFromToken);
      }
      if (orgIdFromToken) {
        setOrgId(orgIdFromToken);
        localStorage.setItem("orgId", orgIdFromToken);
      }
      if (DepartmentIdFromToken) {
        setDepartmentId(DepartmentIdFromToken);
        localStorage.setItem("DepartmentId", DepartmentIdFromToken);
      }
      if (userId) {
        const userData = await authRepository.getUserById(userId);
        setUser(userData);
      }
    } catch (error) {
    }
  };

  const forgotpassword = async (email: string) => {
    try {
      await authRepository.forgotpassword(email);
    } catch (error) {
    }
  };

  const logout = () => {
    authRepository.logout();
    setToken(null);
    setUser(null);
    setEmail(null);
    setRole(null);
    setVendorId(null);
    localStorage.clear();
  };

  useEffect(() => {
    if (!token) return;
    try {
      const decoded: any = jwtDecode(token);
      const userId = decoded["userId"] || decoded["id"];
      const roleFromToken =
        decoded["userRole"] ||
        decoded["role"] ||
        decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
      const emailFromToken = decoded["email"] || decoded["sub"];
      const vendorIdFromToken = decoded["vendorId"];
      const orgIdFromToken = decoded["orgId"] || decoded["OrgId"];
      const DepartmentIdFromToken = decoded["departmentId"];
      const locationIdFromtoken = decoded["locationId"];

      if (orgIdFromToken) {
        setOrgId(orgIdFromToken);
        localStorage.setItem("orgId", orgIdFromToken);
      }

      if (locationIdFromtoken) {
        setLocationId(locationIdFromtoken);
        localStorage.setItem("LocationId", locationIdFromtoken);
      }

      if (emailFromToken) {
        setEmail(emailFromToken);
        localStorage.setItem("email", emailFromToken);
      }

      if (roleFromToken) {
        setRole(roleFromToken);
        localStorage.setItem("role", roleFromToken);
      }

      if (vendorIdFromToken) {
        setVendorId(vendorIdFromToken);
        localStorage.setItem("vendorId", vendorIdFromToken);
      }

      if (DepartmentIdFromToken) {
        setDepartmentId(DepartmentIdFromToken);
        localStorage.setItem("DepartmentId", DepartmentIdFromToken);
      }

      if (userId) {
        authRepository
          .getUserById(userId)
          .then((userData) => setUser(userData))
          .catch((error)=>{
            
          });
      }
    } catch (err) {
    }
  }, [token]);


  return (
    <AuthContext.Provider
      value={{ orgId, setUser, DepartmentId, role, LocationId, token, email, user, vendorId, login, signup, forgotpassword, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  console.log(context, "dfg")
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};